// src/components/BoardPack/pages/MainTable.jsx
import React, { useState, useEffect, useRef } from "react";
import { TableCurrentMonthYear } from "../components/tables/TableCurrentMonthYear";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import { useQueue } from "@/context/QueueContext";
import NoDataComponent from "@/components/common/NoDataComponent";
import { processParams } from "../utils/paramsHelper";
import { useNavigate } from "react-router-dom";

function MainTable({
  year = "",
  month = "",
  monthNumber = "",
  contract = "",
  page = null,
  isExporting = false, // ✅ export mode
  cfoComment = "", // ✅ lifted state from BoardPack
  onCfoCommentChange, // ✅ setter from BoardPack
  isRefresh = false,
}) {
  const { enqueue } = useQueue();
  const navigate = useNavigate();
  //   const [cfoComments, setCfoComments] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState({ columns: [], rows: [] });
  const hasFetchedRef = useRef(false);

  const [summary, setSummary] = useState(null);

  const is_cfo = page?.params?.is_cfo ? true : false;
  const measure = page?.params?.measure ? page?.params?.measure : 0;
  // const hasFetchedRef = useRef(false);
  const hasComment = cfoComment.trim() !== "";
  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call your centralized apiCall function
      const apiParams = processParams(page?.params || {}, { year, month, contract });

      const response = await enqueue(() =>
        apiCall("GET", "/board-pack/page-one", apiParams, navigate, false, false, isRefresh)
      );

      if (!response?.success) {
        throw new Error(response?.message || "API returned unsuccessful response");
      }

      const { meta, data: apiRows } = response;
      if (response?.ai_summary || response?.lines) {
        setSummary({
          ai_summary: response?.ai_summary || "",
          lines: response?.lines || [],
        });
      }

      // Fixed column structure (matches your original layout)
      const columns = [
        { year: meta.year, period: meta.month, type: "Actual" },
        { year: meta.year, period: meta.month, type: "Budget" },
        { year: meta.year, period: meta.month, type: "Variance Act V Bud" },
        { year: meta.year, period: meta.month, type: "% Variance Act V Bud" },
        { year: meta.year, period: meta.month, type: "Actual" },
        { year: meta.previousYear, period: meta.month, type: "Actual" },
        {
          year: `${meta.year} v ${meta.previousYear}`,
          period: meta.month,
          type: "Variance",
        },
        {
          year: `${meta.year} v ${meta.previousYear}`,
          period: meta.month,
          type: "% Variance",
        },
        {
          year: meta.year,
          period: `${meta.month} YTD`,
          type: "Actual",
          isYTD: true,
        },
        {
          year: meta.previousYear,
          period: `${meta.month} YTD`,
          type: "Actual",
          isYTD: true,
        },
        {
          year: `${meta.year} v ${meta.previousYear}`,
          period: `${meta.month} YTD`,
          type: "Variance",
          isYTD: true,
        },
        {
          year: `${meta.year} v ${meta.previousYear}`,
          period: `${meta.month} YTD`,
          type: "% Variance",
          isYTD: true,
        },
        {
          year: meta.year,
          period: `${meta.month} YTD`,
          type: "Actual",
          isYTD: true,
        },
        {
          year: meta.year,
          period: `${meta.month} YTD`,
          type: "Budget",
          isYTD: true,
        },
        {
          year: meta.year,
          period: `${meta.month} YTD`,
          type: "Variance Act V Bud",
          isYTD: true,
        },
        {
          year: meta.year,
          period: `${meta.month} YTD`,
          type: "% Variance Act V Bud",
          isYTD: true,
        },
      ];

      // Transform API response to table rows format
      const transformedRows = apiRows.map((item) => {
        const isTotal =
          item.label === "Total Revenue" ||
          item.label === "EBITDA" ||
          item.label === "OPERATING PROFIT/(LOSS)";

        const isSectionHeader =
          item.label === "Revenue" ||
          item.label === "Total Rent" ||
          item.label === "Zakat, Exceptional & CG" ||
          item.label === "Total Depreciation & Amortization";

        return {
          label: item.label,
          values: [
            item.block1.Actual || "-",
            item.block1.Budget || "-",
            item.block1.Variance || "-",
            item.block1["Variance %"] || "-",

            item.block2.Actual || "-",
            item.block2.Budget || "-",
            item.block2.Variance || "-",
            item.block2["Variance %"] || "-",

            item.block3.Actual || "-",
            item.block3.Budget || "-",
            item.block3.Variance || "-",
            item.block3["Variance %"] || "-",

            item.block4.Actual || "-",
            item.block4.Budget || "-",
            item.block4.Variance || "-",
            item.block4["Variance %"] || "-",
          ],
          isTotal,
          isSectionHeader,
        };
      });

      setTableData({
        columns,
        rows: transformedRows,
      });
    } catch (err) {
      console.error("Profit & Loss fetch error:", err);
      setError(err.message || "Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  // ────────────────────────────────────────────────
  // Render states
  // ────────────────────────────────────────────────

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }
  // if(tableData.rows){
  // return <NoDataComponent  onRetry={fetchData} />
  // }

  return (
   
        <TableCurrentMonthYear
          title={page?.title}
          subtitle={page?.subtitle}
          columns={tableData.columns}
          rows={tableData.rows}
          isCFO={is_cfo}
        />
  );
}

export default MainTable;
